import React, { FC, useState, useMemo, useEffect, useContext } from "react";
import DrawerForms from "@pixel/core/DrawerForms";
import Box from "@pixel/core/Box";
import FormInputLayout from "@pixel/core/FormInputLayout";
import { AutoSelect } from "@pixel/core";
import TextField from "@pixel/core/TextField";
import ImageUpload from '@pixel/core/ImageUpload';
import { useMultipleLocalizedText } from "../../../contexts/Localization/localizationHooks";
import axios from "axios";
import OdsContext from '../OdsContext';
import { useSnackbar } from "@pixel/core/SnackbarService";
import { useConfirmation, DialogLabelSet } from '@pixel/core/ConfirmationService';
import { IAddBusinessUnitPayload, IBusinessData } from '../OdsTypes';
import CircularProgress from "@pixel/core/CircularProgress";
import { filterOutCurrentParentBU, fecthWords } from "../OdsTransform";
import { checkForAnyChangesInForm } from "../OdsTransform";

interface AddBusinessDrawerProps {
  addDrawerState: boolean;
  setaddDrawerState: any;
  selectedBU: IBusinessData;
  editSelectedBU: IAddBusinessUnitPayload;
  BUHierarchyTree: any;
  duplicateSelectedBU: boolean;
  setDuplicateSelectedBU: any;
  makeBusinessUnitActive: any;
  makeBusinessUnitInActive: any;
}

const AddBusinessUnit: FC<AddBusinessDrawerProps> = ({
  addDrawerState,
  setaddDrawerState,
  selectedBU,
  editSelectedBU,
  BUHierarchyTree,
  duplicateSelectedBU,
  setDuplicateSelectedBU,
  makeBusinessUnitActive,
  makeBusinessUnitInActive,
}) => {
  const {
    ODS_BUSINESS_UNIT_SETTINGS,
    ODS_CANCEL_BUTTON_CAPTION,
    ODS_SAVE_BUTTON_CAPTION,
    ODS_DUPLICATE_CAPTION,
    ODS_MAKE_ACTIVE_CAPTION,
    ODS_MAKE_IN_ACTIVE_CAPTION,
    ODS_BUSINESS_UNIT_NAME,
    ODS_PARENT_BUSINESS_UNIT,
    ODS_SELECT_PARENT_BUSINESS_UNIT,
    ODS_DESCRIPTION_CAPTION,
    ODS_CAREER_SITE_URL,
    ODS_PRIVACY_NOTICE_URL,
    ODS_REMOVE_IMAGE_BUTTON,
    ODS_EMAIL_LOGO,
    ODS_IMAGE_UPLOAD_HELPER_TEXT,
    ODS_IMAGE_UPLOAD_CAPTION,
    ODS_UPLOAD_IMAGE_BUTTON,
    ODS_SOCIAL_MEDIA_LOGO,
    ODS_TWITTER_LOGO,
    ODS_OKAY_BUTTON_TEXT,
    ODS_IMAGE_UPLOAD_ERROR_TEXT,
    ODS_IMAGE_UPLOAD_HELPER_TEXT_1,
    ODS_IMAGE_UPLOAD_HELPER_TEXT_2,
    ODS_LEAVE_FORM_CONFIRMATION_TITLE,
    ODS_LEAVE_FORM_CONFIRMATION_DESC,
    ODS_BU_NAME_REQUIRED,
    ODS_PARENT_BU_REQUIRED,
    ODS_BU_SUBMITTED_SUCCESSFULLY,
    ODS_BU_SUBMIT_FAILED,
    ODS_BU_UDATED_SUCCESSFULLY,
    ODS_BU_UPDATE_FAILED,
    ODS_BU_DUPLICATE_NAME_ERROR,
    ODS_ACTIONS_MAKE_INACTIVE,
    ODS_ACTIONS_MAKE_ACTIVE,
    ODS_ACTIONS_DUPLICATE,
  } = useMultipleLocalizedText()
  const snack = useSnackbar();
  const confirm = useConfirmation();
  const OrgElementsContext = useContext(OdsContext);

  const addBusinessUnitPayload: IAddBusinessUnitPayload = useMemo(() => ({
    parentEId: '',
    name: '',
    description: '',
    emailLogoUrl: null,
    socialMediaLogoUrl: null,
    twitterLogoUrl: null,
    careerSiteUrl: '',
    privacyNoticeUrl: '',
  }), []);

  const overlayStyling = {
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    alignItems: 'center',
    zIndex: '1',
  }

  const resetDrawerState = () => {
    setaddDrawerState(false);
    setEmailLogoError(false);
    setEmailLogoErrorText(null);
    setSocialMediaLogoError(false);
    setSocialMediaLogoErrorText(null);
    setTwitterLogoError(false);
    setTwitterLogoErrorText(null);
    setEmailLogoUrl('');
    setSocialMediaLogoUrl('');
    setTwitterLogoUrl('');
    setSelectedParentBU(null);
    setDuplicateBULogo(false);
    setFormError({ parentEId: false, name: false });
    setAddBUPayload(addBusinessUnitPayload);
  }

  const [logoUploadPercentage, setLogoUploadPercentage] = useState(0);
  const [addBUPayload, setAddBUPayload] = useState<IAddBusinessUnitPayload>(addBusinessUnitPayload);
  const [selectedParentBU, setSelectedParentBU] = useState(null);
  const [formError, setFormError] = useState({ parentEId: false, name: false });
  const [parentBUList, setParentBUList] = useState(null);
  const [addBUInProgress, setAddBUInProgress] = useState(false);
  const [parentBUDropDownState, setParentBUDropDownState] = useState(false);
  const [showDuplicateErrorName, setShowDuplicateErrorName] = useState(false);
  const [duplicateBULogo, setDuplicateBULogo] = useState(false);

  const [emailLogoUrl, setEmailLogoUrl] = useState(null);
  const [emailLogoError, setEmailLogoError] = useState(false);
  const [emailLogoErrorText, setEmailLogoErrorText] = useState(null);
  const [emailLogoUploading, setEmailLogoUploading] = useState(false);

  const [socialMediaLogoUrl, setSocialMediaLogoUrl] = useState(null);
  const [socialMediaLogoError, setSocialMediaLogoError] = useState(false);
  const [socialMediaLogoErrorText, setSocialMediaLogoErrorText] = useState(null);
  const [socialMediaUploading, setSocialMediaUploading] = useState(false);

  const [twitterLogoUrl, setTwitterLogoUrl] = useState(null);
  const [twitterLogoError, setTwitterLogoError] = useState(false);
  const [twitterLogoErrorText, setTwitterLogoErrorText] = useState(null);
  const [twitterLogoUploading, setTwitterLogoUploading] = useState(false);

  const [formState, setFormState] = useState<IAddBusinessUnitPayload>(addBUPayload);
  const columnsToSkip = ['actions', 'active', 'eid'];
  const handleAddBusinessDrawerClose = () => {
    const onLoadDrawerData = editSelectedBU || duplicateSelectedBU ? editSelectedBU : formState;
    const isChanged = checkForAnyChangesInForm(addBUPayload, onLoadDrawerData, columnsToSkip);
    if (!isChanged) {
      resetDrawerState();
      setParentBUDropDownState(false);
      setDuplicateSelectedBU(null);
    } else {
      confirm.open({
        dialogLabelSet: DialogLabelSet.CONFIRM,
        title: ODS_LEAVE_FORM_CONFIRMATION_TITLE,
        description: ODS_LEAVE_FORM_CONFIRMATION_DESC,
        onResolve: (reason: string) => {
          resetDrawerState();
          setParentBUDropDownState(false);
          setDuplicateSelectedBU(null);
        },
        onReject: (reason: string) => {
          console.log("callback rejected reason: " + reason);
        }
      });
    }
  };

  const setBusinessUnitLogo = (id: String, fileObj: Blob, logoUrl: string) => {
    if (id === 'isEmailLogo') {
      setEmailLogoUrl(logoUrl);
    } else if (id === 'isFBLogo') {
      setTwitterLogoUrl(logoUrl);
    } else {
      setSocialMediaLogoUrl(logoUrl);
    }
    setEmailLogoUploading(false);
    setSocialMediaUploading(false);
    setTwitterLogoUploading(false);
  };

  useEffect(() => {
    setAddBUPayload({
      ...addBUPayload,
      'emailLogoUrl': emailLogoUrl,
      'twitterLogoUrl': twitterLogoUrl,
      'socialMediaLogoUrl': socialMediaLogoUrl,
    })
  }, [emailLogoUrl, twitterLogoUrl, socialMediaLogoUrl]);

  const setLogoUploadErrors = (id: String, errorText: string) => {
    if (id === 'isEmailLogo') {
      setEmailLogoError(true);
      setEmailLogoErrorText(errorText);
    } else if (id === 'isFBLogo') {
      setTwitterLogoError(true);
      setTwitterLogoErrorText(errorText);
    } else {
      setSocialMediaLogoError(true);
      setSocialMediaLogoErrorText(errorText);
    }
    setEmailLogoUploading(false);
    setSocialMediaUploading(false);
    setTwitterLogoUploading(false);
  }

  const duplicateAddBusinessMoreAction = () => {
    setAddBUPayload({
      ...addBUPayload,
      name: '[Copy] '+addBUPayload.name,
    });
    setDuplicateSelectedBU(true);
    UploadLogoForDuplicateBU(addBUPayload);
  };

  const validateForm = () => {
    let temp: any = {};
    temp.parentEId = addBUPayload.parentEId === ''
    temp.name = addBUPayload.name === ''
    setFormError({
      ...temp
    });
    return Object.values(temp).every(x => x == '');
  }

  const onAddBusinessSaveForm = () => {
    if (validateForm()) {
      setAddBUInProgress(true);
      try {
        if(editSelectedBU && !duplicateSelectedBU){
          setAddBUPayload({...addBUPayload, eid: editSelectedBU.eid});
          addBUPayload.eid = editSelectedBU.eid;
          axios
          .put(window.jvUrls.admin + "/action/ods/businessUnits/" + editSelectedBU.eid, addBUPayload, {
            headers: {'Content-Type': 'application/json'}
          })
          .then(function (response) {
            setAddBUInProgress(false);
            if ([201, 200].includes(response.status)) {
              resetDrawerState();
              setParentBUDropDownState(false)
              snack.showSuccess(ODS_BU_UDATED_SUCCESSFULLY, OrgElementsContext.snackBarAutoHideTime);
            } else {
              setAddBUInProgress(false);
              snack.showError(ODS_BU_UPDATE_FAILED, OrgElementsContext.snackBarAutoHideTime);
            }
          })
          .catch(function (error) {
            setAddBUInProgress(false);
            snack.showError(error.response.data.errorMessage, OrgElementsContext.snackBarAutoHideTime);
            if(error.response.data.errorMessage.search('name is already present') != -1){
              setFormError({ ...formError, name: true });
              setShowDuplicateErrorName(true)
            }
          });
        } else {
          axios
          .post(window.jvUrls.admin + "/action/ods/businessUnits", editSelectedBU ? {...addBUPayload, active: editSelectedBU.active} : addBUPayload, {
            headers: {
              'Content-Type': 'application/json; charset=UTF-8'
            }
          })
          .then(function (response) {
            setAddBUInProgress(false);
            setDuplicateSelectedBU(null);
            if ([201, 200].includes(response.status)) {
              resetDrawerState();
              snack.showSuccess(ODS_BU_SUBMITTED_SUCCESSFULLY, OrgElementsContext.snackBarAutoHideTime);
            } else {
              setAddBUInProgress(false);
              snack.showError(ODS_BU_SUBMIT_FAILED, OrgElementsContext.snackBarAutoHideTime);
            }
          })
          .catch(function (error) {
            setAddBUInProgress(false);
            setDuplicateSelectedBU(null);
            snack.showError(error.response.data.errorMessage, OrgElementsContext.snackBarAutoHideTime);
            if(error.response.data.errorMessage.search('name is already present') != -1){
              setFormError({ ...formError, name: true });
              setShowDuplicateErrorName(true)
            }
          });
        }
      } catch (error) {
        setAddBUInProgress(false);
        console.log(error);
      }
    }
  }

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setShowDuplicateErrorName(false);
    setAddBUPayload({ ...addBUPayload, [name]: value });
    setFormError({ ...formError, name: false });
  }

  const handleAutoSelectChange = (event: any, newValue: any) => {
    let parentEid = '';
    setSelectedParentBU(newValue);
    if (newValue && newValue.name && newValue.eid === '') {
      parentEid = null;
    } else if (newValue == null) {
      parentEid = '';
    } else {
      parentEid = newValue.eid;
    }
    setAddBUPayload({ ...addBUPayload, 'parentEId': parentEid });
    setFormError({ ...formError, parentEId: false });
  }

  const uploadLogo = async (fileObj: Blob, id?: string) => {
    const formData = new FormData();
    formData.append(id, 'true');
    formData.append("subsidiaryEId", ''); // need to add logic for subsidiary EID logic 
    formData.append("qqfile", fileObj);
    const onUploadProgress = (event: any) => {
      let percentCompleted = Math.round((event.loaded * 100) / event.total);
      setLogoUploadPercentage(percentCompleted);
    };
    await axios.post(window.jvUrls.admin + '/action/admin/company/profileLogo?imageType=Company&getDownloadToken=true', formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      },
      onUploadProgress
    })
      .then(function (response) {
        if (response.data.result.logoImageURL) {
          setBusinessUnitLogo(id, fileObj, response.data.result.logoImageURL);
        } else {
          let errorText = Object.keys(response.data.result).map(key => response.data.result[key])[0];
          setLogoUploadErrors(id, errorText);
        }
      })
      .catch(function (error) {
        setLogoUploadErrors(id, error.response.statusText);
      });
  }

  const fetchImage = async (url: string, name: string) => {
    const res = await fetch(url);
    const blob = await res.blob();
    const imageBlob = new Blob([blob], { type: "image/"+url.split(".").pop()});
    const imageObjectURL =  new File([imageBlob], name+'.'+url.split(".").pop(), {type: imageBlob.type});
    return imageObjectURL;
  };

  const UploadLogoForDuplicateBU = async(bu: IAddBusinessUnitPayload) => {
    if(bu.emailLogoUrl){
      let emailLogoFileObj = await fetchImage(bu.emailLogoUrl, fecthWords(bu.name, 1)+' emailLogo');
      handleEmailLogoChange(emailLogoFileObj);
    };
    if(bu.socialMediaLogoUrl){
      let SMLogoFileObj = await fetchImage(bu.socialMediaLogoUrl, fecthWords(bu.name, 3)+' SMLogo');
      handleSocialMediaLogoChange(SMLogoFileObj);
    }
    if(bu.twitterLogoUrl){
      let twitterLogoFileObj = await fetchImage(bu.twitterLogoUrl, fecthWords(bu.name, 3)+' twitterLogo'); 
      handleTwitterLogoChange(twitterLogoFileObj);
    }
  };

  const populateFormForEdit = (bu: IAddBusinessUnitPayload) => {
    setAddBUPayload({
      ...addBUPayload,
      parentEId: (selectedBU && selectedBU.ancestors) ? selectedBU.ancestors[selectedBU.ancestors.length-1].eid : bu.parentEId,
      name: duplicateSelectedBU ? '[Copy] '+bu.name : bu.name,
      description: bu.description,
      emailLogoUrl: bu.emailLogoUrl == null ? '' : bu.emailLogoUrl,
      socialMediaLogoUrl: bu.socialMediaLogoUrl == null ? '' : bu.socialMediaLogoUrl,
      twitterLogoUrl: bu.twitterLogoUrl == null ? '' : bu.twitterLogoUrl,
      careerSiteUrl: bu.careerSiteUrl,
      privacyNoticeUrl: bu.privacyNoticeUrl,
    });
    BUHierarchyTree.length === 1 && (selectedBU && selectedBU.ancestors === null) || selectedBU.name.trim().toLowerCase() === selectedBU.parentName.trim().toLowerCase() ? setParentBUDropDownState(true) : setParentBUDropDownState(false);
    BUHierarchyTree.length !== 1 && setSelectedParentBU({ name: (selectedBU && selectedBU.ancestors) ? selectedBU.ancestors[selectedBU.ancestors.length-1].name : bu.parentName, eid: (selectedBU && selectedBU.ancestors) ? selectedBU.ancestors[selectedBU.ancestors.length-1].eid : bu.parentEId });
    if(duplicateSelectedBU){
      setDuplicateBULogo(true);
    } else {
      bu.emailLogoUrl && setEmailLogoUrl(bu.emailLogoUrl);
      bu.socialMediaLogoUrl && setSocialMediaLogoUrl(bu.socialMediaLogoUrl);
      bu.twitterLogoUrl && setTwitterLogoUrl(bu.twitterLogoUrl);
    }
  };

  const fetchParentBUList = async () => {
    try {
      await axios
        .get(window.jvUrls.admin + "/action/ods/businessUnits/parentBUs")
        .then(function (response) {
          if(editSelectedBU) {
            setParentBUList(filterOutCurrentParentBU(response.data, editSelectedBU.eid));
            populateFormForEdit(editSelectedBU);
          } else {
            setParentBUList(response.data)
          }
        })
        .catch(function (error) {
          console.log(error);
        });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (selectedBU || (editSelectedBU && editSelectedBU.parentEId)) {
      setAddBUPayload({ ...addBUPayload, parentEId: editSelectedBU ? editSelectedBU.parentEId : selectedBU.eid });
      setSelectedParentBU({ name: editSelectedBU ? editSelectedBU.parentName : selectedBU.name, eid: editSelectedBU ? editSelectedBU.parentEId : selectedBU.eid });
      parentBUList && setFormState({ ...addBUPayload, parentEId: editSelectedBU ? editSelectedBU.parentEId : selectedBU.eid })
    } else if (!editSelectedBU) {
      let parentCompany = JSON.parse(sessionStorage.getItem("BU_company"));
      parentBUList && setAddBUPayload({ ...addBUPayload, parentEId: (selectedBU && selectedBU.ancestors) ? selectedBU.ancestors[selectedBU.ancestors.length-1].eid : parentCompany.eid });
      parentBUList && setSelectedParentBU({name: (selectedBU && selectedBU.ancestors) ? selectedBU.ancestors[selectedBU.ancestors.length-1].name : parentCompany.name, eid: (selectedBU && selectedBU.ancestors) ? selectedBU.ancestors[selectedBU.ancestors.length-1].eid : parentCompany.eid });
      parentBUList && setFormState({ ...addBUPayload, parentEId: (selectedBU && selectedBU.ancestors) ? selectedBU.ancestors[selectedBU.ancestors.length-1].eid : parentCompany.eid })
    }
  }, [parentBUList]);

  useEffect(() => {
    if(duplicateBULogo){
      UploadLogoForDuplicateBU(addBUPayload);
    }
  }, [duplicateBULogo]);

  useEffect(() => {
    addDrawerState && fetchParentBUList();
  }, [addDrawerState]);


  const handleSocialMediaLogoChange = (file: Blob) => {
    setSocialMediaUploading(true);
    uploadLogo(file, 'isSocialMedia');
  };

  const handleEmailLogoChange = (file: Blob) => {
    setEmailLogoUploading(true);
    uploadLogo(file, 'isEmailLogo');
  }

  const handleTwitterLogoChange = (file: Blob) => {
    setTwitterLogoUploading(true);
    uploadLogo(file, 'isFBLogo');
  }

  const handleMakeBusinessUnitInactive = (selectedBU: IBusinessData) => {
    makeBusinessUnitInActive(selectedBU);
    resetDrawerState();
  }

  const handleMakeBusinessUnitActive = (selectedBU: IBusinessData) => {
    makeBusinessUnitActive(selectedBU);
    resetDrawerState();
  }

  const populateActionsList = (actionList: any) => {
    const BUActions = [
      { i18Label: ODS_ACTIONS_DUPLICATE, name: "duplicate" },
      { i18Label: ODS_ACTIONS_MAKE_ACTIVE, name: "makeActive" },
      { i18Label: ODS_ACTIONS_MAKE_INACTIVE, name: "makeInactive" },
    ];
    const actionLinks: any = [];
    if (actionList) {
      Object.keys(actionList).forEach((key) => {
        let filteredAction = BUActions.filter((e) => e.name === key);
        if (filteredAction.length > 0 && actionList[key] !== 'hidden') {
          let actionTertiaryLink = {
            type: "tertiary",
            label: filteredAction[0].i18Label,
            disabled: actionList[key] === 'grayout',
            onClick: (event: any, data: any) => {
              filteredAction[0].name === 'duplicate' && duplicateAddBusinessMoreAction();
              filteredAction[0].name === 'makeActive' && handleMakeBusinessUnitActive(selectedBU);
              filteredAction[0].name === 'makeInactive' && handleMakeBusinessUnitInactive(selectedBU);
            }
          };
          actionLinks.push(actionTertiaryLink);
        }
      });
    }
    return actionLinks;
  }

  const toggleEmailLogoErrorState = () => { setEmailLogoError(false); setEmailLogoErrorText(null) };
  const toggleSocialMediaErrorState = () => { setSocialMediaLogoError(false); setSocialMediaLogoErrorText(null) };
  const toggleTwitterLogoErrorState = () => { setTwitterLogoError(false); setTwitterLogoErrorText(null) };
  const onRemoveEmailLogo = () => { setEmailLogoUrl(''); setAddBUPayload({ ...addBUPayload, emailLogoUrl:''})};
  const onRemoveSocialMediaLogo = () => { setSocialMediaLogoUrl('');  setAddBUPayload({ ...addBUPayload, socialMediaLogoUrl: ''})};
  const onRemoveTwitterLogo = () => { setTwitterLogoUrl(''); setAddBUPayload({ ...addBUPayload, twitterLogoUrl: ''})};

  const DrawerPartionStyle = {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',

    "& .drawer-container-left-section": {
      "& > .MuiBox-root": {
        marginTop: 2,
      },
      width: '68%',
      overflow: 'auto',
      paddingRight: '24px',
      marginRight: '24px',
      borderRight: '1px solid lightgrey',
    },

    "& .drawer-container-right-section": {
      width: '32%',
      overflow: 'auto',
    },
  };

  return (
    <div>
      <DrawerForms
        labelDrawerForm={ODS_BUSINESS_UNIT_SETTINGS}
        labelSaveDrawerForm={editSelectedBU && !duplicateSelectedBU ? 'Update' : ODS_SAVE_BUTTON_CAPTION}
        labelClose={ODS_CANCEL_BUTTON_CAPTION}
        anchor="right"
        drawerWidth="40%"
        onSaveForm={onAddBusinessSaveForm}
        onCancel={handleAddBusinessDrawerClose}
        open={addDrawerState}
        SelectedActionGroupProps={editSelectedBU && !duplicateSelectedBU && {
          id: 'drawer-from-actions',
          variant: 'standard_more',
          disableIconButton: false,
          actionLabel: 'Custom drawer actions',
          actions: populateActionsList(editSelectedBU.actions)
        }}
      >
        {(addBUInProgress && <Box sx={overlayStyling}><CircularProgress size={60} /></Box>)}
        <Box sx={DrawerPartionStyle} className={'admin-ODS-drawer-container'}>
          <Box className={'drawer-container-left-section'}>
            <Box className={'bu-name-text-field'}><TextField label={ODS_BUSINESS_UNIT_NAME} maxLength={200} error={formError.name} errorText={showDuplicateErrorName ? ODS_BU_DUPLICATE_NAME_ERROR : ODS_BU_NAME_REQUIRED} name="name" onChange={handleInputChange} value={addBUPayload.name} fullWidth required></TextField></Box>
            <Box className={'bu-parent-name-field'}>
              <AutoSelect
                fullWidth
                required
                id="parentBusinessUnit"
                label={ODS_PARENT_BUSINESS_UNIT}
                name="parentEId"
                disabled={parentBUDropDownState}
                value={selectedParentBU}
                options={parentBUList}
                getOptionLabel={(option: any) => option.name}
                onChange={handleAutoSelectChange}
                isOptionEqualToValue={(option: any, value: any) => option.eid === value.eid}
                error={formError.parentEId}
                errorText={ODS_PARENT_BU_REQUIRED}
                placeholder={ODS_SELECT_PARENT_BUSINESS_UNIT}
              />
            </Box>
            <Box className={'bu-description-field'}><TextField onChange={handleInputChange} name="description" value={addBUPayload.description} label={ODS_DESCRIPTION_CAPTION} multiline rows={6} fullWidth ></TextField></Box>
            <Box className={'bu-careersite-url-field'} ><TextField label={ODS_CAREER_SITE_URL} name="careerSiteUrl" onChange={handleInputChange} value={addBUPayload.careerSiteUrl} fullWidth ></TextField></Box>
            <Box className={'bu-privacy-url-field'} ><TextField value={addBUPayload.privacyNoticeUrl} name="privacyNoticeUrl" onChange={handleInputChange} label={ODS_PRIVACY_NOTICE_URL} fullWidth ></TextField></Box>
          </Box>
          <Box className={'drawer-container-right-section'}>
            <Box className={'bu-email-logo-upload'}>
            <FormInputLayout>
              <ImageUpload
                id={'emailLogo'}
                crossIconButtonText={ODS_REMOVE_IMAGE_BUTTON}
                imageUploadCaption={ODS_EMAIL_LOGO}
                width={'165px'}
                height={'165px'}
                error={emailLogoError}
                isImageUploading={emailLogoUploading}
                uploadProgressPercentage={logoUploadPercentage}
                errorText={emailLogoErrorText}
                errorConfirmationText={ODS_OKAY_BUTTON_TEXT}
                imageUploaderHelperText={ODS_IMAGE_UPLOAD_HELPER_TEXT_1}
                uploadButtonCaption={ODS_IMAGE_UPLOAD_CAPTION}
                imageUploadtitle={ODS_UPLOAD_IMAGE_BUTTON}
                acceptedImageFormats={'image/png, image/gif, image/jpeg'}
                imageUrl={emailLogoUrl}
                onCloseIcon={onRemoveEmailLogo}
                handleFileChange={handleEmailLogoChange}
                onToggleErrorState={toggleEmailLogoErrorState}
              /></FormInputLayout>
            </Box>
            <Box className={'bu-sm-logo-upload'}>
            <FormInputLayout>
              <ImageUpload
                id={'socialMediaLogo'}
                crossIconButtonText={ODS_REMOVE_IMAGE_BUTTON}
                imageUploadCaption={ODS_SOCIAL_MEDIA_LOGO}
                width={'165px'}
                height={'165px'}
                error={socialMediaLogoError}
                errorText={socialMediaLogoErrorText}
                errorConfirmationText={ODS_OKAY_BUTTON_TEXT}
                isImageUploading={socialMediaUploading}
                uploadProgressPercentage={logoUploadPercentage}
                imageUploaderHelperText={ODS_IMAGE_UPLOAD_HELPER_TEXT_2}
                uploadButtonCaption={ODS_IMAGE_UPLOAD_CAPTION}
                imageUploadtitle={ODS_UPLOAD_IMAGE_BUTTON}
                acceptedImageFormats={'image/png, image/gif, image/jpeg'}
                imageUrl={socialMediaLogoUrl}
                onCloseIcon={onRemoveSocialMediaLogo}
                handleFileChange={handleSocialMediaLogoChange}
                onToggleErrorState={toggleSocialMediaErrorState}
              />
            </FormInputLayout>
            </Box>
            {/* <Box className={'bu-twitter-logo-upload'}>
            <FormInputLayout>
              <ImageUpload
                id={'twitterLogo'}
                crossIconButtonText={ODS_REMOVE_IMAGE_BUTTON}
                imageUploadCaption={ODS_TWITTER_LOGO}
                width={'165px'}
                height={'165px'}
                error={twitterLogoError}
                errorText={twitterLogoErrorText}
                errorConfirmationText={ODS_OKAY_BUTTON_TEXT}
                isImageUploading={twitterLogoUploading}
                uploadProgressPercentage={logoUploadPercentage}
                imageUploaderHelperText={ODS_IMAGE_UPLOAD_HELPER_TEXT}
                uploadButtonCaption={ODS_IMAGE_UPLOAD_CAPTION}
                imageUploadtitle={ODS_UPLOAD_IMAGE_BUTTON}
                acceptedImageFormats={'image/png, image/gif, image/jpeg'}
                imageUrl={twitterLogoUrl}
                onCloseIcon={onRemoveTwitterLogo}
                handleFileChange={handleTwitterLogoChange}
                onToggleErrorState={toggleTwitterLogoErrorState}
              />
            </FormInputLayout>
            </Box> */}
          </Box>
        </Box>
      </DrawerForms>
    </div>
  );
};

export default AddBusinessUnit;